import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { defaultRouteForUser, isPortalUser, canAccessAdmin } from "@/lib/routes";

/**
 * Guards a route.
 *  - `roles`        : if set, the user's role must be in the list
 *  - `portalOnly`   : route is reserved for self-service employees (principal_type === 'employee')
 *  - `adminOnly`    : route is reserved for admin principals (principal_type === 'user'),
 *                     OR admin-capable portal roles like senior_management / hod
 *  - `allowMustChange` : skip the must_change_password gate (used by /change-password itself)
 */
export default function ProtectedRoute({
  children,
  roles,
  portalOnly = false,
  adminOnly = false,
  allowMustChange = false,
}) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center text-slate-500 text-sm"
      >
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force first-time users to set a new password before accessing the app.
  if (!allowMustChange && user.must_change_password)
    return <Navigate to="/change-password" replace />;
  // Portal vs admin separation
  if (portalOnly && !isPortalUser(user)) {
    return <Navigate to="/" replace />;
  }
  // adminOnly: allow real admins AND admin-capable portal roles (SM/HOD).
  if (adminOnly && !canAccessAdmin(user)) {
    return <Navigate to={defaultRouteForUser(user)} replace />;
  }
  // Role allow-list (only meaningful for admin-side roles)
  // Rev. 2026-02-08 — When `"hr"` is on the allow-list, transparently admit
  // the newer maker-checker roles `hr_admin` and `hr_maker` too. This
  // avoids having to touch 27+ Route declarations in App.js every time
  // an HR role is added. Any route that trusts `"hr"` should also trust
  // the modern HR roles unless it's explicitly super-admin-only.
  if (roles && roles.length) {
    const expanded = new Set(roles);
    if (expanded.has("hr")) {
      expanded.add("hr_admin");
      expanded.add("hr_maker");
    }
    if (!expanded.has(user.role)) {
      return <Navigate to={defaultRouteForUser(user)} replace />;
    }
  }
  return children;
}
